import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Plus, Edit, CreditCard, Info } from 'lucide-react';
import type { AppDispatch, RootState } from '@/store';
import {
  fetchRateCardsByYear,
  createRateCard,
  updateRateCard,
  setCurrentYear,
  clearError,
} from '@/store/slices/rateCardsSlice';
import { fetchResourceLevels } from '@/store/slices/resourceLevelsSlice';
import { DataTable } from '@/components/ui';
import {
  GlassCard,
  GlassBadge,
  GlassSelect,
  GlassPageHeader,
  GlassModal,
  GlassLoadingSpinner,
  GlassAlert,
  SectionReveal,
} from '@/design-system';
import RateCardForm from './components/RateCardForm';
import type { RateCard, ResourceLevel, Column } from '@/types';
import { formatCurrencyCompact } from '@/utils/formatters';

const RateCardsPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { entities: rateCards, currentYear, loading, error } = useSelector((state: RootState) => state.rateCards);
  const { entities: resourceLevels } = useSelector((state: RootState) => state.resourceLevels);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedRateCard, setSelectedRateCard] = useState<RateCard | null>(null);
  const [presetLevelId, setPresetLevelId] = useState<string | undefined>(undefined);

  useEffect(() => {
    dispatch(fetchResourceLevels(false));
    dispatch(fetchRateCardsByYear(currentYear));
  }, [dispatch, currentYear]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        dispatch(clearError());
      }, 5000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [error, dispatch]);

  const handleYearChange = (year: string) => {
    dispatch(setCurrentYear(Number(year)));
  };

  const handleCreateRateCard = async (data: Partial<RateCard>) => {
    const result = await dispatch(createRateCard(data));
    if (createRateCard.fulfilled.match(result)) {
      setIsCreateModalOpen(false);
      setPresetLevelId(undefined);
      dispatch(fetchRateCardsByYear(currentYear));
    }
  };

  const handleUpdateRateCard = async (data: Partial<RateCard>) => {
    if (!selectedRateCard) return;
    const result = await dispatch(updateRateCard({ id: selectedRateCard.id, data }));
    if (updateRateCard.fulfilled.match(result)) {
      setIsEditModalOpen(false);
      setSelectedRateCard(null);
      dispatch(fetchRateCardsByYear(currentYear));
    }
  };

  const handleAddRate = (levelId: string) => {
    setPresetLevelId(levelId);
    setIsCreateModalOpen(true);
  };

  const handleEditRate = (rate: RateCard) => {
    setSelectedRateCard(rate);
    setIsEditModalOpen(true);
  };

  const yearOptions = Array.from({ length: 11 }, (_, i) => {
    const year = 2020 + i;
    return { value: year.toString(), label: `FY ${year}` };
  });

  // Combine resource levels with their rates
  const activeResourceLevels = resourceLevels.filter(rl => rl.isActive);
  const tableData = activeResourceLevels.map((level) => {
    const rate = rateCards.find((r) => r.resourceLevelId === level.id && r.year === currentYear);
    return {
      ...level,
      rate,
      hourlyRate: rate ? Number(rate.hourlyRate) : null,
      annualCost: rate ? Number(rate.hourlyRate) * 40 * 52 : null,
    };
  });

  // Table columns
  const columns: Column<typeof tableData[0]>[] = [
    {
      key: 'displayName',
      header: 'Resource Level',
      sortable: true,
      render: (value, row) => (
        <div>
          <div className="font-medium text-[var(--text)]">{value}</div>
          <div className="text-xs text-[var(--muted)]">{row.code}</div>
        </div>
      )
    },
    {
      key: 'category',
      header: 'Category',
      render: (value) => (
        <GlassBadge variant={value === 'MANAGEMENT' ? 'info' : 'default'} size="sm">
          {value}
        </GlassBadge>
      )
    },
    {
      key: 'hourlyRate',
      header: 'Hourly Rate',
      sortable: true,
      render: (value) => (
        value !== null ? (
          <span className="text-accent-emerald font-semibold">
            ${value.toFixed(2)}/hr
          </span>
        ) : (
          <GlassBadge variant="warning" size="sm">Not Set</GlassBadge>
        )
      )
    },
    {
      key: 'annualCost',
      header: 'Annual Cost (FTE)',
      sortable: true,
      render: (value) => (
        value !== null ? (
          <span className="text-[var(--text)] font-medium">
            {formatCurrencyCompact(value)}
          </span>
        ) : (
          <span className="text-[var(--muted)]">-</span>
        )
      )
    }
  ];

  const tableActions = [
    {
      label: 'Edit Rate',
      icon: Edit,
      onClick: (row: typeof tableData[0]) => {
        if (row.rate) {
          handleEditRate(row.rate);
        } else {
          handleAddRate(row.id);
        }
      }
    }
  ];

  if (loading && resourceLevels.length === 0) {
    return (
      <div className="min-h-screen glass-page-bg flex items-center justify-center">
        <GlassLoadingSpinner size="lg" variant="gradient" label="Loading rate cards..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen glass-page-bg p-6 lg:p-8">
      <SectionReveal>
        <GlassPageHeader
          title="Rate Cards"
          subtitle="Manage hourly rates for resource levels by fiscal year"
          icon={<CreditCard className="w-6 h-6" />}
          iconGradient="emerald"
          className="mb-6"
          actions={
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <span className="text-sm text-[var(--muted)]">Fiscal Year:</span>
                <GlassSelect
                  value={currentYear.toString()}
                  onChange={handleYearChange}
                  options={yearOptions}
                  className="w-32"
                />
              </div>
            </div>
          }
        />
      </SectionReveal>

      {error && (
        <SectionReveal>
          <GlassAlert
            variant="error"
            message={error}
            onClose={() => dispatch(clearError())}
            className="mb-6"
          />
        </SectionReveal>
      )}

      {/* Rate Cards Table */}
      <SectionReveal delay={0.1}>
        <GlassCard padding="none">
          <div className="px-6 py-4 border-b border-[var(--border)]">
            <h3 className="text-lg font-semibold text-[var(--text)]">
              Hourly Rates for FY {currentYear}
            </h3>
            <p className="text-sm text-[var(--muted)]">
              Configure rates for each resource level. Annual cost assumes 40 hrs/week x 52 weeks.
            </p>
          </div>
          <div className="max-h-[calc(100vh-400px)] overflow-auto">
            <DataTable
              data={tableData}
              columns={columns}
              actions={tableActions}
              sortable
              pagination
              pageSize={15}
              emptyMessage="No resource levels found"
            />
          </div>
        </GlassCard>
      </SectionReveal>

      {/* Info Card */}
      <SectionReveal delay={0.2}>
        <GlassCard className="mt-6">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-xl bg-accent-blue/10">
              <Info className="w-5 h-5 text-accent-blue" />
            </div>
            <div>
              <h4 className="font-semibold text-[var(--text)] mb-1">Rate Card Information</h4>
              <p className="text-sm text-[var(--muted)]">
                Rate cards define the hourly billing rates for each resource level. These rates are used to calculate
                costs for team allocations and resource planning. Each fiscal year requires its own rate configuration.
                Missing rates will prevent accurate cost calculations for those resource levels.
              </p>
            </div>
          </div>
        </GlassCard>
      </SectionReveal>

      {/* Floating Add Rate button */}
      <button
        className="fixed bottom-6 right-6 h-14 w-14 rounded-full shadow-lg bg-gradient-to-r from-accent-emerald to-cyan-500 text-white hover:shadow-xl hover:scale-105 transition-all duration-200 flex items-center justify-center"
        onClick={() => {
          setPresetLevelId(undefined);
          setIsCreateModalOpen(true);
        }}
        aria-label="Add Rate Card"
      >
        <Plus className="w-6 h-6" />
      </button>

      {/* Create Modal */}
      <GlassModal
        isOpen={isCreateModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false);
          setPresetLevelId(undefined);
        }}
        title="Add Rate Card"
        subtitle={`Configure hourly rate for FY ${currentYear}`}
        icon={<CreditCard className="w-5 h-5" />}
        iconGradient="emerald"
        size="md"
      >
        <RateCardForm
          onSubmit={handleCreateRateCard}
          onCancel={() => {
            setIsCreateModalOpen(false);
            setPresetLevelId(undefined);
          }}
          isLoading={loading}
          presetLevelId={presetLevelId}
          presetYear={currentYear}
        />
      </GlassModal>

      {/* Edit Modal */}
      <GlassModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedRateCard(null);
        }}
        title="Edit Rate Card"
        subtitle={selectedRateCard ? `Update hourly rate for FY ${selectedRateCard.year}` : undefined}
        icon={<Edit className="w-5 h-5" />}
        iconGradient="blue-violet"
        size="md"
      >
        <RateCardForm
          initialData={selectedRateCard}
          onSubmit={handleUpdateRateCard}
          onCancel={() => {
            setIsEditModalOpen(false);
            setSelectedRateCard(null);
          }}
          isLoading={loading}
        />
      </GlassModal>
    </div>
  );
};

export default RateCardsPage;
